<template>
  <div id="dialog-planShop">
    <div class="dialog">
      <span class="cls" @click="oth_showPlanShop(false)"></span>
      <div class="paper">
        <!-- 헤더 -->
        <div class="header">
          <h2>🔥 추천플랜 스토어</h2>
          <button class="close-btn" @click="oth_showPlanShop(false)">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>

        <!-- 필터 & 검색 -->
        <div class="filter-bar">
          <div class="filter-tabs">
            <button 
              v-for="cat in categories" :key="cat.key"
              :class="['tab-btn', { active: activeCategory === cat.key }]"
              @click="activeCategory = cat.key"
            >{{ cat.label }}</button>
          </div>
          <div class="search-box">
            <i class="ph-bold ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="플랜 이름, 보험사로 검색..."
            />
            <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">
              <i class="ph ph-x"></i>
            </button>
          </div>
        </div>

        <!-- 플랜 리스트 -->
        <div class="content" ref="listContainer">
          <div v-if="loading" class="loading">
            <i class="ph-bold ph-spinner"></i>
            <span>불러오는 중...</span>
          </div>

          <div v-else-if="filteredList.length === 0" class="empty">
            <i class="ph-bold ph-magnifying-glass"></i>
            <span>검색 결과가 없습니다</span>
          </div>

          <ul v-else class="plan-grid">
            <li 
              v-for="plan in filteredList" :key="plan.idx"
              @click="onPlanClick(plan)"
            >
              <!-- 카드 상단: 로고 + 타이틀 + 등급 -->
              <div class="card-top">
                <img class="card-logo" :src="plan.image" v-if="plan.image" />
                <div class="card-logo-ph" v-else>
                  <i class="ph ph-buildings"></i>
                </div>
                <div class="card-title">
                  <span class="company">{{ plan.company }}</span>
                  <h3>{{ plan.title }}</h3>
                </div>
                <div class="card-grade" :class="'grade-' + plan.rating">
                  {{ plan.rating }}
                </div>
              </div>

              <!-- 카드 중간: 한줄 remarks 태그 -->
              <div class="card-remarks" v-if="plan.remarkTags.length > 0">
                <span 
                  v-for="(tag, ti) in plan.remarkTags" :key="ti"
                  class="rtag"
                  :class="'rtag-' + tag.color"
                >{{ tag.text }}</span>
              </div>
              <div class="card-remarks card-remarks-empty" v-else>
                <span class="no-remarks">메모 없음</span>
              </div>

              <!-- 카드 하단: 가격 + 다운로드 -->
              <div class="card-footer">
                <span class="price">{{ plan.price }}</span>
                <span class="downloads"><i class="ph ph-download"></i> {{ plan.downloads }}건</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue';

export default {
  name: 'planShop',
  props: {
    oth_showPlanShop: {
      type: Function,
      required: true
    },
    oth_openPlanView: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const getInsuCompanyInfo = inject('getInsuCompanyInfo');

    const loading = ref(false);
    const planList = ref([]);
    const searchQuery = ref('');
    const activeCategory = ref('all');

    const categories = [
      { key: 'all', label: '전체' },
      { key: 'child', label: '어린이' },
      { key: 'health', label: '건강' },
      { key: 'life', label: '종신' },
      { key: 'car', label: '자동차' },
      { key: 'fire', label: '화재' },
    ];

    // ━━━ 담보 기반 태그 생성을 위한 데이터 ━━━
    const chapterListData = ref({});     // 챕터 트리
    const coverageMap = ref({});         // { plan_idx: [coverages] }
    const exCoverageMap = ref(new Map()); // 원본 담보 맵

    /**
     * ━━━ 자동 태그 생성 기준 ━━━
     * 여기만 수정하면 자동 태그가 바뀜
     * type: count, chapter_max, chapter_sum, chapter_count, total_max, total_avg, price, rate, renewable
     * match: 챕터 이름 키워드 (chapter_ 타입 전용)
     * threshold: 기준값
     * below: 기준보다 낮을 때 태그 (null이면 안 보여줌)
     * above: 기준보다 높거나 같을 때 태그 (null이면 안 보여줌)
     * eq/tag: 정확히 일치 (renewable 등)
     */
    const autoCriteria = [
      { type: 'count',         threshold: 15,    below: 'y:보장항목 적음',   above: 'g:보장항목 많음' },

      { type: 'chapter_max',   match: '암',      threshold: 3000,  below: 'r:암보장 부족',    above: 'g:암보장 좋음' },
      { type: 'chapter_max',   match: '뇌',      threshold: 3000,  below: 'r:뇌보장 부족',    above: 'g:뇌보장 좋음' },
      { type: 'chapter_max',   match: '심장',    threshold: 3000,  below: 'r:심장보장 부족',   above: 'g:심장보장 좋음' },
      { type: 'chapter_max',   match: '수술',    threshold: 500,   below: 'r:수술비 부족',    above: 'g:수술비 좋음' },
      { type: 'chapter_max',   match: '입원',    threshold: 10,    below: 'r:입원비 부족',    above: 'g:입원비 좋음' },
      { type: 'chapter_max',   match: '사망',    threshold: 10000, below: 'y:사망보장 낮음',   above: 'g:사망보장 높음' },
      { type: 'chapter_max',   match: '후유장해', threshold: 10000, below: 'y:후유장해 낮음',   above: 'g:후유장해 높음' },
      { type: 'chapter_max',   match: '진단',    threshold: 2000,  below: 'y:진단금 낮음',    above: 'g:진단금 좋음' },

      { type: 'chapter_count', match: '실손',    threshold: 1,     below: 'r:실손 미가입',    above: 'b:실손 가입' },

      { type: 'total_avg',     threshold: 1500,  below: 'y:평균 보장금 낮음', above: 'g:평균 보장금 높음' },

      { type: 'price',         threshold: 80000, below: null,               above: 'y:높은 보험료' },
      { type: 'price',         threshold: 30000, below: 'g:저렴한 보험료',   above: null },

      { type: 'rate',          threshold: 85,    below: null,               above: 'g:높은 평점' },
      { type: 'rate',          threshold: 60,    below: 'r:낮은 평점',      above: null },

      { type: 'renewable',     eq: 0, tag: 'g:비갱신형' },
      { type: 'renewable',     eq: 1, tag: 'y:갱신형' },
    ];

    /** 챕터 이름 키워드로 담보 찾기 */
    function getCoveragesByKeyword(covList, keyword) {
      const matchedIdxs = [];
      for (const root of Object.values(chapterListData.value)) {
        if (root.name.includes(keyword)) {
          Object.values(root.list).forEach(child => matchedIdxs.push(child.idx));
        }
        for (const child of Object.values(root.list)) {
          if (child.name.includes(keyword)) matchedIdxs.push(child.idx);
        }
      }
      return covList.filter(c => matchedIdxs.includes(c.chapter_idx));
    }

    /** 담보 + 플랜 기반 자동 태그 생성 */
    function generateAutoRemarks(planRaw) {
      const tags = [];
      const covList = (coverageMap.value[planRaw.idx] || []).filter(c => !c.is_union && !c.low_show);
      const values = covList.map(c => Number(c.value) || 0);

      for (const rule of autoCriteria) {
        // eq 타입
        if (rule.eq !== undefined) {
          if (String(planRaw[rule.type]) === String(rule.eq)) tags.push(...parseRemarks(rule.tag));
          continue;
        }

        let val = null;
        switch (rule.type) {
          case 'count':
            val = covList.length; break;
          case 'chapter_count': {
            val = getCoveragesByKeyword(covList, rule.match).length; break;
          }
          case 'chapter_max': {
            const m = getCoveragesByKeyword(covList, rule.match);
            if (m.length === 0) continue;
            val = Math.max(...m.map(c => Number(c.value) || 0)); break;
          }
          case 'chapter_sum': {
            const m = getCoveragesByKeyword(covList, rule.match);
            if (m.length === 0) continue;
            val = m.reduce((s, c) => s + (Number(c.value) || 0), 0); break;
          }
          case 'total_max':
            if (values.length === 0) continue;
            val = Math.max(...values); break;
          case 'total_avg':
            if (values.length === 0) continue;
            val = values.reduce((a, b) => a + b, 0) / values.length; break;
          case 'price':
            val = Number(planRaw.price) || null; break;
          case 'rate':
            val = Number(planRaw.rate) || null; break;
          default: continue;
        }
        if (val === null) continue;

        if (val < rule.threshold && rule.below) tags.push(...parseRemarks(rule.below));
        else if (val >= rule.threshold && rule.above) tags.push(...parseRemarks(rule.above));
      }
      return tags;
    }

    /**
     * remarks 파싱
     * DB 저장 포맷: "g:유병자 O|r:높은 가격|b:무해지|일반 텍스트"
     * g: = green (장점), r: = red (단점), b: = blue (정보), y: = yellow (주의), 접두사 없으면 default
     */
    function parseRemarks(remarks) {
      if (!remarks || !remarks.trim()) return [];
      const colorMap = { 'g': 'green', 'r': 'red', 'b': 'blue', 'y': 'yellow' };
      return remarks.split('|').map(item => {
        const trimmed = item.trim();
        if (!trimmed) return null;
        const match = trimmed.match(/^([grby]):(.+)$/);
        if (match) {
          return { color: colorMap[match[1]], text: match[2].trim() };
        }
        return { color: 'default', text: trimmed };
      }).filter(Boolean);
    }

    function formatMoney(val) {
      if (!val) return '0';
      return Number(val).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    function getRating(rate) {
      if (rate >= 90) return 'S+';
      if (rate >= 80) return 'A+';
      if (rate >= 70) return 'A';
      if (rate >= 60) return 'B';
      return 'C';
    }

    const mappedPlans = computed(() => {
      return planList.value.map((plan) => {
        const companyInfo = getInsuCompanyInfo ? getInsuCompanyInfo(plan.company) : null;
        const parsed = parseRemarks(plan.remarks);
        return {
          idx: plan.idx,
          title: plan.name,
          company: companyInfo?.name || plan.company || '보험사',
          image: companyInfo?.short_img || '',
          price: formatMoney(plan.price) + '원',
          rating: getRating(plan.rate || 60),
          downloads: Math.floor(Math.random() * 50) + 10,
          remarkTags: parsed.length > 0 ? parsed : generateAutoRemarks(plan),
          category: plan.category || 'all',
          raw: plan
        };
      });
    });

    const filteredList = computed(() => {
      let list = mappedPlans.value;
      if (activeCategory.value !== 'all') {
        list = list.filter(p => p.category === activeCategory.value);
      }
      if (searchQuery.value.trim()) {
        const q = searchQuery.value.trim().toLowerCase();
        list = list.filter(p =>
          p.title.toLowerCase().includes(q) ||
          p.company.toLowerCase().includes(q)
        );
      }
      return list;
    });

    // 챕터 트리 로드
    async function fetchChapters() {
      const roots = await sql_query(`SELECT * FROM IS_chapter WHERE (parent IS NULL OR parent = 0) AND status = 1 ORDER BY no ASC`);
      const children = await sql_query(`SELECT * FROM IS_chapter WHERE parent IS NOT NULL AND parent != 0 AND status = 1 ORDER BY no ASC`);
      const tree = {};
      for (const ch of roots) {
        tree[ch.en_name] = { idx: ch.idx, no: ch.no, name: ch.name, list: {} };
      }
      for (const ch of children) {
        const parent = roots.find(r => r.idx === ch.parent);
        if (parent && tree[parent.en_name]) {
          tree[parent.en_name].list[ch.en_name] = { name: ch.name, idx: ch.idx, no: ch.no };
        }
      }
      chapterListData.value = tree;
    }

    // 플랜 목록의 담보 배치 로드
    async function fetchBatchCoverages(planIds) {
      if (planIds.length === 0) return;

      // 원본 담보 (한번만)
      if (exCoverageMap.value.size === 0) {
        const originals = await sql_query(`SELECT * FROM IS_ex_coverage`);
        exCoverageMap.value = new Map((originals || []).map(o => [o.idx, o]));
      }

      const ids = planIds.join(',');
      const rows = await sql_query(`SELECT * FROM IS_coverage WHERE plan_id IN (${ids}) AND visible = 1 ORDER BY idx DESC`);
      const map = {};
      for (const cov of (rows || [])) {
        const orig = exCoverageMap.value.get(cov.original);
        const item = {
          idx: cov.idx,
          chapter_idx: cov.chapter_id || orig?.chapter_id,
          value: cov.value,
          is_union: cov.is_union || 0,
          low_show: cov.low_show || 0,
        };
        if (!map[cov.plan_id]) map[cov.plan_id] = [];
        map[cov.plan_id].push(item);
      }
      coverageMap.value = map;
    }

    async function fetchPlans() {
      loading.value = true;
      try {
        // 챕터 먼저
        await fetchChapters();

        const sql = `SELECT * FROM IS_plan WHERE public = '1' ORDER BY idx DESC LIMIT 50`;
        const result = await sql_query(sql);
        planList.value = (result || []).map(row => ({
          idx: row.idx,
          mb_id: row.mb_id,
          name: row.name,
          company: row.company,
          price: row.price,
          rate: row.rate || 60,
          remarks: row.remarks || '',
          renewable: row.renewable,
          type: row.type || '',
          worth: row.worth || 0,
          category: row.category || 'all',
          created_at: row.created_at,
        }));

        // 담보 배치 로드
        const planIds = planList.value.map(p => p.idx);
        await fetchBatchCoverages(planIds);
      } catch (e) {
        console.error('플랜 로드 실패:', e);
      } finally {
        loading.value = false;
      }
    }

    function onPlanClick(plan) {
      props.oth_openPlanView(plan.idx);
    }

    onMounted(() => {
      fetchPlans();
    });

    return {
      loading,
      searchQuery,
      activeCategory,
      categories,
      filteredList,
      onPlanClick,
    };
  }
}
</script>

<style scoped>
#dialog-planShop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
}

.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(6px);
}

.cls {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
}

.paper {
  width: 90%;
  max-width: 720px;
  height: 85vh;
  background: light-dark(#ffffff, #141418);
  border: 1px solid light-dark(#e8e8e8, #2a2a30);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0,0,0,0.2);
}

/* ===== 헤더 ===== */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid light-dark(#f0f0f0, #222);
  flex-shrink: 0;
}

.header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-extrabold);
  color: light-dark(#111, #f0f0f0);
}

.close-btn {
  width: 34px;
  height: 34px;
  background: light-dark(#f0f0f0, #2a2a30);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: light-dark(#555, #bbb);
  font-size: var(--font-size-md);
  border-radius: 50%;
  transition: all 0.15s;
}

.close-btn:hover {
  background: light-dark(#e0e0e0, #3a3a40);
}

/* ===== 필터 바 ===== */
.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 22px;
  border-bottom: 1px solid light-dark(#f0f0f0, #222);
  flex-shrink: 0;
}

.filter-tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
}

.filter-tabs::-webkit-scrollbar { display: none; }

.tab-btn {
  padding: 6px 14px;
  border-radius: 100px;
  border: 1.5px solid light-dark(#e0e0e0, #333);
  background: light-dark(#fff, #1a1a1f);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: light-dark(#666, #999);
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.tab-btn:hover {
  border-color: light-dark(#bbb, #555);
}

.tab-btn.active {
  background: light-dark(#111, var(--color-brand));
  color: light-dark(#fff, #000);
  border-color: light-dark(#111, var(--color-brand));
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: light-dark(#f5f5f5, #1e1e24);
  border-radius: var(--radius-md);
  border: 1.5px solid light-dark(transparent, #2a2a30);
  transition: border-color 0.15s;
}

.search-box:focus-within {
  border-color: var(--color-brand);
}

.search-box i {
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  color: light-dark(#111, #eee);
  outline: none;
}

.search-box input::placeholder { color: var(--color-text-secondary); }

.search-clear {
  width: 20px;
  height: 20px;
  border: none;
  background: light-dark(#ddd, #444);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.search-clear i {
  font-size: 10px;
  color: light-dark(#666, #ccc);
}

/* ===== 컨텐츠 ===== */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 22px;
}

.content::-webkit-scrollbar { width: 4px; }
.content::-webkit-scrollbar-thumb { background: var(--color-text-secondary); border-radius: 2px; }

.loading, .empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: var(--color-text-secondary);
}

.loading i {
  font-size: 24px;
  animation: spin 1s linear infinite;
}

.empty i {
  font-size: 36px;
  opacity: 0.35;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== 플랜 그리드 ===== */
.plan-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.plan-grid > li {
  flex: 1 1 calc(50% - 6px);
  min-width: 260px;
  background: light-dark(#ffffff, #1a1a1f);
  border: 1.5px solid light-dark(#eaeaea, #2a2a30);
  border-radius: var(--radius-lg);
  padding: 14px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.plan-grid > li:hover {
  border-color: var(--color-brand);
  box-shadow: 0 4px 16px light-dark(rgba(0,0,0,0.08), rgba(0,0,0,0.3));
}

.plan-grid > li:active {
  transform: scale(0.985);
}

/* 카드 상단 */
.card-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-logo {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  object-fit: contain;
  flex-shrink: 0;
  background: light-dark(#f8f8f8, #252528);
}

.card-logo-ph {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: light-dark(#f0f0f0, #252528);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-logo-ph i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.card-title {
  flex: 1;
  min-width: 0;
}

.card-title .company {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: block;
  margin-bottom: 2px;
}

.card-title h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-extrabold);
  margin: 0;
  color: light-dark(#111, #f0f0f0);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-grade {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-black);
  flex-shrink: 0;
  letter-spacing: -0.5px;
}

.card-grade.grade-S\+ { background: rgba(91,123,240,0.12); color: #5B7BF0; }
.card-grade.grade-A\+ { background: rgba(76,175,80,0.12); color: #4CAF50; }
.card-grade.grade-A { background: rgba(102,187,106,0.12); color: #66BB6A; }
.card-grade.grade-B { background: rgba(255,152,0,0.12); color: #FF9800; }
.card-grade.grade-C { background: rgba(244,67,54,0.12); color: #F44336; }

/* ===== Remark Tags ===== */
.card-remarks {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding-top: 10px;
  border-top: 1px solid light-dark(#f0f0f0, #2a2a30);
}

.card-remarks-empty {
  justify-content: center;
}

.no-remarks {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  opacity: 0.5;
}

.rtag {
  display: inline-block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 2px 9px;
  border-radius: 100px;
  line-height: 1.7;
  white-space: nowrap;
}

.rtag-green {
  background: rgba(34, 197, 94, 0.12);
  color: #22C55E;
}

.rtag-red {
  background: rgba(239, 68, 68, 0.12);
  color: #EF4444;
}

.rtag-blue {
  background: rgba(59, 130, 246, 0.12);
  color: #3B82F6;
}

.rtag-yellow {
  background: rgba(245, 158, 11, 0.12);
  color: #F59E0B;
}

.rtag-default {
  background: light-dark(rgba(0,0,0,0.05), rgba(255,255,255,0.08));
  color: light-dark(#555, #aaa);
}

/* 카드 하단 */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid light-dark(#f0f0f0, #2a2a30);
}

.price {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: light-dark(#111, #f0f0f0);
}

.downloads {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ===== 반응형 ===== */
@media (max-width: 1024px), (max-height: 768px) {
  .paper {
    width: 95%;
    max-width: 640px;
    height: 88vh;
    border-radius: 16px;
  }

  .header { padding: 14px 18px; }
  .filter-bar { padding: 10px 18px; gap: 8px; }
  .content { padding: 12px 18px; }

  .plan-grid { gap: 10px; }

  .plan-grid > li {
    padding: 12px;
    gap: 10px;
  }

  .card-logo, .card-logo-ph {
    width: 42px;
    height: 42px;
    border-radius: 10px;
  }

  .card-title h3 {
    font-size: var(--font-size-base);
  }

  .card-grade {
    width: 34px;
    height: 34px;
    font-size: var(--font-size-sm);
  }
}

@media (max-width: 600px) {
  .paper {
    width: 100%;
    height: 100%;
    max-width: 100%;
    border-radius: 0;
  }

  .plan-grid > li {
    flex: 1 1 100%;
    min-width: 0;
  }

  .filter-bar {
    gap: 8px;
  }
}
</style>